import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import ProductCard from "../components/ProductCard";
import { Skeleton } from "../components/ui/skeleton";
import { Search } from "lucide-react";
import { Input } from "../components/ui/input";

const categories = [
  { id: "all", name: "Tutti" },
  { id: "infissi", name: "Infissi" },
  { id: "porte", name: "Porte" },
  { id: "cucine", name: "Cucine" },
  { id: "arredamento", name: "Arredo" },
  { id: "pergole", name: "Pergole" },
  { id: "parquet", name: "Parquet" },
  { id: "pavimenti", name: "Pavimenti" },
  { id: "outdoor", name: "Outdoor" },
];

const ShopPage = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const params = { 
          outlet: false,
          available: true,
          ...(selectedCategory !== "all" && { category: selectedCategory })
        };
        const response = await axios.get(`${API}/products`, { 
          params,
          headers: { Authorization: `Bearer ${token}` }
        });
        setProducts(response.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [token, selectedCategory]);

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-black" data-testid="shop-page">
      {/* Header */}
      <div className="p-4 space-y-4 border-b border-white/10">
        <h1 className="text-2xl font-light text-white tracking-wide">Shop</h1>
        <p className="text-sm text-white/60">Prodotti in pronta consegna</p>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
          <Input
            type="text"
            placeholder="Cerca prodotti..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-white h-11 focus:border-emerald-500"
            data-testid="shop-search"
          />
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                selectedCategory === cat.id
                  ? "bg-emerald-500 text-black font-medium"
                  : "bg-zinc-900 text-white/70 hover:bg-zinc-800"
              }`}
              data-testid={`category-${cat.id}`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div className="p-4">
        {loading ? (
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-square w-full bg-zinc-800" />
                <Skeleton className="h-3 w-3/4 bg-zinc-800" />
                <Skeleton className="h-4 w-1/2 bg-zinc-800" />
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20 text-white/60">
            <p className="text-lg mb-2">Nessun prodotto trovato</p>
            <p className="text-sm">Prova a modificare i filtri di ricerca</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
